import { useState, useEffect, useCallback } from 'react';
import { httpClient } from '../common/httpclient';

// Cache configuration
const CACHE_KEY = 'user_permissions_cache';

// Permissões de Configurações (uma `view` e uma `update` por sessão: Ordem de Serviço, Vendas,
// Gerais, Integrações). Qualquer uma delas libera a tela /settings e o item de menu.
export const SETTINGS_PERMISSIONS = [
  'settings.serviceorder.view', 'settings.serviceorder.update',
  'settings.sale.view', 'settings.sale.update',
  'settings.general.view', 'settings.general.update',
  'settings.integration.view', 'settings.integration.update',
];
const CACHE_EXPIRATION_TIME = 10; // 5 minutes in milliseconds

/**
 * Hook para gerenciar permissões do usuário baseado no endpoint /me
 * @returns {Object} Objeto contendo permissões, dados do usuário e métodos de validação
 */
export const usePermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [userData, setUserData] = useState(null);
  const [modules, setModules] = useState([]);
  const [trialExpired, setTrialExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Decodifica o JWT token e retorna os dados do payload
   * @param {string} token - JWT token
   * @returns {Object|null} Dados decodificados do token ou null se inválido
   */
  const decodeToken = useCallback((token) => {
    try {
      if (!token) return null;
      
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = parts[1];
      const decoded = JSON.parse(atob(payload));
      
      return decoded;
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }, []);

  /**
   * Verifica se o token está expirado
   * @param {Object} tokenData - Dados decodificados do token
   * @returns {boolean} true se expirado, false caso contrário
   */
  const isTokenExpired = useCallback((tokenData) => {
    if (!tokenData || !tokenData.exp) return true;
    
    const now = Math.floor(Date.now() / 1000);
    return tokenData.exp <= now;
  }, []);

  /**
   * Obtém o cache de permissões
   * @returns {Object|null} Dados do cache ou null se inválido/expirado
   */
  const getCachedPermissions = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const now = Date.now();

      // Verificar se o cache expirou
      if (now - cacheData.timestamp > CACHE_EXPIRATION_TIME) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.error('Erro ao ler cache de permissões:', error);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  }, []);

  /**
   * Salva as permissões no cache
   * @param {Object} data - Dados do usuário e permissões
   */
  const setCachedPermissions = useCallback((data) => {
    try {
      const cacheData = {
        timestamp: Date.now(),
        data: data
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Erro ao salvar cache de permissões:', error);
    }
  }, []);

  /**
   * Limpa o cache de permissões
   */
  const clearCachedPermissions = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
  }, []);

  /**
   * Carrega as permissões do endpoint /me com cache
   */
  const loadPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      
      if (!token) {
        clearCachedPermissions();
        setPermissions([]);
        setUserData(null);
        setModules([]);
        setLoading(false);
        return;
      }

      // Verificar se o token é válido (mas não limpar imediatamente se expirado)
      // Deixa o interceptor do httpClient tentar fazer refresh primeiro
      const tokenData = decodeToken(token);
      
      if (!tokenData) {
        // Token inválido (não conseguiu decodificar), limpar dados e cache
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        clearCachedPermissions();
        setPermissions([]);
        setUserData(null);
        setModules([]);
        setLoading(false);
        return;
      }
      
      // Se o token estiver expirado, ainda tenta fazer a requisição
      // O interceptor do httpClient vai tentar fazer refresh automaticamente
      // Se o refresh falhar, o interceptor vai redirecionar para login

      // Tentar obter do cache primeiro
      const cachedData = getCachedPermissions();
      if (cachedData) {
        setUserData(cachedData.userData);
        setPermissions(cachedData.permissions || []);
        setModules(cachedData.modules || []);
        setTrialExpired(cachedData.trialExpired === true);
        setLoading(false);
        return;
      }

      // Se não houver cache válido, buscar do endpoint
      try {
        const response = await httpClient.get('/me');
        
        if (response.status === 200 && response.data) {
          const apiData = response.data;

          // Mapear dados do usuário
          const user = {
            id: apiData.id,
            username: apiData.username,
            name: apiData.name,
            last_name: apiData.last_name,
            profile_id: apiData.profile_id,
            profile_name: apiData.profile_name,
            is_admin: apiData.is_admin || false,
            sys_admin: apiData.sys_admin || false,
            is_owner: apiData.is_owner || false
          };

          // Extrair permissões da resposta
          const apiPermissions = apiData.permissions || [];

          // Extrair módulos contratados no plano da empresa (fonte de verdade: backend)
          const apiModules = Array.isArray(apiData.modules) ? apiData.modules : [];

          // Salvar no cache
          setCachedPermissions({
            userData: user,
            permissions: apiPermissions,
            modules: apiModules,
            trialExpired: apiData.trial_expired === true
          });

          setUserData(user);
          setPermissions(apiPermissions);
          setModules(apiModules);
          setTrialExpired(apiData.trial_expired === true);
        } else {
          throw new Error('Resposta inválida do endpoint /me');
        }
      } catch (apiError) {
        console.error('Erro ao buscar permissões do endpoint /me:', apiError);
        
        // Se houver erro 401 ou 403, limpar tokens e cache
        if (apiError.response?.status === 401 || apiError.response?.status === 403) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          clearCachedPermissions();
          setPermissions([]);
          setUserData(null);
          setModules([]);
        } else {
          setError(apiError.message || 'Erro ao carregar permissões');
        }
      }

    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [decodeToken, isTokenExpired, getCachedPermissions, setCachedPermissions, clearCachedPermissions]);

  /**
   * Verifica se um módulo está ativo (contratado no plano da empresa)
   * @param {string} module - Nome do módulo a ser verificado
   * @returns {boolean} true se o módulo está ativo, false caso contrário
   */
  const hasModule = useCallback((module) => {
    if (!module) return false;
    return modules.includes(module);
  }, [modules]);

  /**
   * Verifica se uma permissão pertence a algum módulo do plano.
   * Mapeados apenas os módulos com regra de plano já validada de ponta a ponta
   * (backend confirmado retornando o valor real em /me). Outros mod_* existem no
   * plano mas não há garantia de que todo cliente já tenha essas colunas
   * configuradas corretamente — mapear os demais aqui sem auditar os dados
   * esconderia telas que clientes já usam hoje.
   *
   * NOTA (checklist): `mod_checklist` (plan_items) existe no backend mas nunca é
   * exposto/editável na UI de Admin > Planos (só mod_suppliers, mod_customer,
   * mod_program_loyalty, mod_order_service, mod_dashboard, mod_report e
   * mod_vehicle têm checkbox lá) e a coluna nasceu com DEFAULT false sem
   * backfill. Ou seja, para todo cliente hoje esse módulo é permanentemente
   * false e inalcançável — gatear `/checklist*` por ele apenas escondia o menu
   * "Checklists" e os botões de ação em vehicles/checklist/List.js e View.js
   * mesmo para quem tem a permissão de perfil, sem nunca ter restringido nada
   * de fato (o preenchimento de checklist em ordens de serviço já é liberado
   * só pelo toggle `os_use_checklist` de Configurações, sem checar módulo).
   * Por isso `checklist.*` não é mapeado para módulo — depende só da
   * permissão de perfil/admin, como a maioria das demais telas.
   *
   * As permissões são as chaves de negócio do catálogo (fv-api/RBAC.md, ex.:
   * `sale.create`); o mapeamento é por prefixo da chave (seção 1.4 do RBAC.md).
   */
  const getModuleForPermission = useCallback((permission) => {
    if (!permission) return null;

    if (
      permission.startsWith('vehicle.') ||
      permission === 'report.vehicle' ||
      permission === 'dashboard.vehicle'
    ) return 'vehicle';
    // Inventário de estoque: módulo `mod_inventory` do plano (backfill = true
    // para planos existentes na migration, então clientes atuais não perdem a tela).
    if (permission.startsWith('inventory.') || permission === 'report.inventory') return 'inventory';

    // Módulos do catálogo de planos (planmodule.Catalog no backend). Todos nasceram
    // com backfill = true para os planos existentes, então só some a tela de quem o
    // plano desliga explicitamente. WhatsApp e Marketplace não têm permissão de perfil
    // própria: o gate deles é `hasModule(...)` direto no menu/tela. O Assistente de IA
    // usa a permissão `ai.use`, que também exige o módulo `ai_assistant`.
    if (permission.startsWith('servicequote.') || permission === 'dashboard.servicequote') return 'quote';
    if (permission.startsWith('ai.')) return 'ai_assistant';
    if (permission.startsWith('sale.')) return 'sale';
    if (
      permission.startsWith('product.') ||
      permission.startsWith('category.') ||
      permission.startsWith('purchase.') ||
      permission === 'dashboard.product'
    ) return 'product';
    if (
      permission.startsWith('accounts.') ||
      permission.startsWith('cashregister.') ||
      permission.startsWith('cardmachine.')
    ) return 'financial';
    if (permission.startsWith('audit.')) return 'audit';

    return null;
  }, []);

  /**
   * Verifica se o usuário tem uma permissão específica.
   * Ordem de checagem: 1) módulo contratado no plano, 2) permissão do perfil ou admin.
   * Um admin não enxerga um módulo que a empresa não contratou.
   * @param {string} permission - Nome da permissão a ser verificada
   * @returns {boolean} true se tem permissão, false caso contrário
   */
  const hasPermission = useCallback((permission) => {
    if (!permission) return false;
    if (!userData) return false;

    // 1) Módulo precisa estar contratado no plano, independente de ser admin
    const requiredModule = getModuleForPermission(permission);
    if (requiredModule && !modules.includes(requiredModule)) return false;

    // 2) Se for admin, tem todas as permissões dos módulos que a empresa possui
    if (userData.is_admin) return true;

    // Verificar se a permissão está na lista de permissões do usuário
    return permissions.includes(permission);
  }, [permissions, userData, modules, getModuleForPermission]);

  /**
   * Verifica se o usuário tem alguma das permissões fornecidas
   * @param {string[]} permissionList - Lista de permissões a serem verificadas
   * @returns {boolean} true se tem pelo menos uma permissão, false caso contrário
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (!permissionList || !Array.isArray(permissionList)) return false;
    if (!userData) return false;

    return permissionList.some(permission => hasPermission(permission));
  }, [userData, hasPermission]);

  /**
   * Verifica se o usuário tem todas as permissões fornecidas
   * @param {string[]} permissionList - Lista de permissões a serem verificadas
   * @returns {boolean} true se tem todas as permissões, false caso contrário
   */
  const hasAllPermissions = useCallback((permissionList) => {
    if (!permissionList || !Array.isArray(permissionList)) return false;
    if (!userData) return false;

    return permissionList.every(permission => hasPermission(permission));
  }, [userData, hasPermission]);

  /**
   * Verifica se o usuário é sys_admin
   * @returns {boolean} true se for sys_admin, false caso contrário
   */
  const isSysAdmin = useCallback(() => {
    if (!userData) return false;
    return userData.sys_admin === true;
  }, [userData]);

  /**
   * Verifica se o usuário está autenticado
   * @returns {boolean} true se autenticado, false caso contrário
   */
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    const tokenData = decodeToken(token);
    return tokenData && !isTokenExpired(tokenData);
  }, [decodeToken, isTokenExpired]);

  /**
   * Força o reload das permissões (limpa o cache e busca novamente)
   */
  const reloadPermissions = useCallback(() => {
    clearCachedPermissions();
    loadPermissions();
  }, [loadPermissions, clearCachedPermissions]);

  // Carregar permissões na inicialização
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Escutar mudanças no localStorage para atualizar permissões
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'access_token' || e.key === 'refresh_token') {
        // Limpar cache quando o token mudar para garantir dados atualizados
        clearCachedPermissions();
        loadPermissions();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadPermissions, clearCachedPermissions]);

  return {
    // Estado
    permissions,
    userData,
    modules,
    trialExpired,
    loading,
    error,

    // Métodos de validação
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasModule,
    isAuthenticated,
    isSysAdmin,
    
    // Utilitários
    reloadPermissions,
    decodeToken
  };
};

export default usePermissions;
